import React, { forwardRef, useImperativeHandle, useCallback, memo } from 'react';
import { Pressable } from 'react-native';

import { useImageViewerActions } from '@/components/ui/image-viewer/ImageViewerProvider';
import { useWindowMeasure } from '@/hooks/useWindowMeasure';
import { Photo } from '@/models/Photo';

/**
 * Props for {@link ImageViewerTrigger} and {@link ImageViewerTriggerWithRef}.
 *
 * Wrap any tappable thumbnail (usually an `expo-image` child). On press, measures the
 * trigger in window coordinates and opens the global image viewer with a morph animation.
 */
interface ImageViewerTriggerProps {
  /** Full-size URI passed to the viewer on open. May differ from the thumbnail child URI. */
  uri: string;
  children: React.ReactNode;
  /** Photo metadata when already available (likes, caption, member, lock state, etc.). */
  photo?: Photo;
  /**
   * When set without `photo`, the provider fetches details on open via React Query.
   * Use for post/profile openers that only have an id up front.
   */
  photoId?: number | string;
  disabled?: boolean;
  /**
   * Carousel source for small, stable lists (e.g. a fixed preview row).
   * Prefer {@link ImageViewerTriggerProps.getPhotos} for large or paginated lists.
   */
  photos?: Photo[];
  /**
   * Lazily resolves carousel photos at open time. Use a ref-backed callback so list
   * items do not receive a new array prop on every pagination or cache update.
   *
   * @example
   * const photosRef = useRef<Photo[]>([]);
   * photosRef.current = photoItems;
   * const getPhotos = useCallback(() => photosRef.current, []);
   */
  getPhotos?: () => Photo[] | undefined;
  className?: string;
  /** Enables download affordance in the viewer (e.g. event photos). */
  allowImageDownload?: boolean;
  /**
   * Opens the viewer without the photo's social actions (likes/comments/caption/
   * context menu); the owner info chip is still shown. Use for images not backed
   * by a Photo record but with a known sender, e.g. conversation attachments.
   */
  disableSocialActions?: boolean;
}

/** Imperative handle exposed by {@link ImageViewerTriggerWithRef}. */
export type ImageViewerTriggerHandle = {
  open: () => void;
};

type ImageViewerTriggerPressProps = Omit<ImageViewerTriggerProps, 'children' | 'className'>;

/** Shared press/measure logic for both trigger variants. */
function useImageViewerTriggerPress({
  uri,
  photo,
  photoId,
  disabled = false,
  photos,
  getPhotos,
  allowImageDownload = false,
  disableSocialActions = false,
}: ImageViewerTriggerPressProps) {
  const { showImage } = useImageViewerActions();
  const { ref: animatedRef, measureInWindow } = useWindowMeasure();

  // Opens the viewer on the JS thread once the trigger's window-coordinate
  // layout has been resolved on the UI thread.
  const openWithLayout = useCallback(
    (x: number, y: number, width: number, height: number) => {
      const carouselPhotos = getPhotos?.() ?? photos;

      showImage(uri, photo, { x, y, width, height }, carouselPhotos, {
        allowImageDownload,
        photoId: photoId && !photo ? photoId : undefined,
        disableSocialActions,
      });
    },
    [uri, photo, photoId, photos, getPhotos, allowImageDownload, disableSocialActions, showImage]
  );

  const handlePress = useCallback(() => {
    if (disabled) return;
    // Measure on the UI thread (see useWindowMeasure) — the JS-thread
    // measureInWindow() path can segfault on Android (GROKIO-APP-145).
    measureInWindow(openWithLayout);
  }, [disabled, measureInWindow, openWithLayout]);

  return { animatedRef, handlePress };
}

function ImageViewerTriggerComponent({
  uri,
  children,
  photo,
  photoId,
  disabled = false,
  photos,
  getPhotos,
  className,
  allowImageDownload = false,
  disableSocialActions = false,
}: ImageViewerTriggerProps) {
  const { animatedRef, handlePress } = useImageViewerTriggerPress({
    uri,
    photo,
    photoId,
    disabled,
    photos,
    getPhotos,
    allowImageDownload,
    disableSocialActions,
  });

  return (
    <Pressable
      ref={animatedRef}
      collapsable={false}
      onPress={handlePress}
      disabled={disabled}
      className={className}>
      {children}
    </Pressable>
  );
}

/**
 * Default image viewer opener for list and feed usage.
 *
 * Subscribes only to {@link useImageViewerActions} (stable context), so opening or
 * closing the viewer does not re-render every mounted trigger. Memoized; no ref overhead.
 *
 * @example Single photo
 * ```tsx
 * <ImageViewerTrigger uri={photo.FullSizeUrl ?? photo.IconUrl} photo={photo}>
 *   <Image source={{ uri: photo.IconUrl }} className="aspect-square w-full" />
 * </ImageViewerTrigger>
 * ```
 *
 * @example Lazy carousel (preferred in FlashList / infinite grids)
 * ```tsx
 * <ImageViewerTrigger
 *   uri={photo.FullSizeUrl ?? photo.IconUrl}
 *   photo={photo}
 *   getPhotos={getPhotosForCarousel}
 * />
 * ```
 */
export const ImageViewerTrigger = memo(ImageViewerTriggerComponent);

/**
 * Ref-capable variant for programmatic open (e.g. locked-photo web overlay click-through).
 * Use only where `ref.open()` is required; otherwise prefer {@link ImageViewerTrigger}.
 */
export const ImageViewerTriggerWithRef = forwardRef<
  ImageViewerTriggerHandle,
  ImageViewerTriggerProps
>(
  (
    {
      uri,
      children,
      photo,
      photoId,
      disabled = false,
      photos,
      getPhotos,
      className,
      allowImageDownload = false,
      disableSocialActions = false,
    },
    ref
  ) => {
    const { animatedRef, handlePress } = useImageViewerTriggerPress({
      uri,
      photo,
      photoId,
      disabled,
      photos,
      getPhotos,
      allowImageDownload,
      disableSocialActions,
    });

    useImperativeHandle(
      ref,
      () => ({
        open: handlePress,
      }),
      [handlePress]
    );

    return (
      <Pressable
        ref={animatedRef}
        collapsable={false}
        onPress={handlePress}
        disabled={disabled}
        className={className}>
        {children}
      </Pressable>
    );
  }
);

ImageViewerTrigger.displayName = 'ImageViewerTrigger';
ImageViewerTriggerWithRef.displayName = 'ImageViewerTriggerWithRef';
